// Live tenure (Alex, 27 Oct 2026): count from the role's start date instead
// of years_at_current_company, which is a snapshot taken at import /
// enrichment and only grows staler. Mirrors liveTenureSQL in
// backend/routes/candidates.js, so the column, sort and filter agree.
//
// current_position_start_date is text: "2021-03" (most), "2021-03-15", or a
// bare year "2021" (counted from January → marked approximate). No start
// date: the snapshot plus the time since it was taken (enriched_at, else
// created_at).

const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;

export interface LiveTenure { years: number; approx: boolean }

function parseStart(raw: unknown): { t: number; yearOnly: boolean } | null {
  const s = raw == null ? '' : String(raw).trim();
  let m = s.match(/^(\d{4})-(0[1-9]|1[0-2])/);
  if (m) return { t: Date.UTC(+m[1], +m[2] - 1, 1), yearOnly: false };
  m = s.match(/^(\d{4})$/);
  if (m) return { t: Date.UTC(+m[1], 0, 1), yearOnly: true };
  return null;
}

export function liveTenure(row: any): LiveTenure | null {
  if (!row) return null;
  const start = parseStart(row.current_position_start_date);
  if (start) return { years: Math.max(0, (Date.now() - start.t) / YEAR_MS), approx: start.yearOnly };
  const snap = row.years_at_current_company;
  if (snap == null || snap === '' || isNaN(Number(snap))) return null;
  const takenAt = Date.parse(row.enriched_at || row.created_at || '');
  const since = Number.isFinite(takenAt) ? Math.max(0, (Date.now() - takenAt) / YEAR_MS) : 0;
  return { years: Number(snap) + since, approx: true };
}

export const liveTenureYears = (row: any): number | null => liveTenure(row)?.years ?? null;

// Months under 2 years ("4 mos"), then years to one decimal ("3.5 yrs").
// approx → "~" in front (bare-year start dates, snapshot estimates).
export function formatTenure(years: number | null | undefined, approx = false): string | null {
  if (years == null || isNaN(Number(years))) return null;
  const y = Number(years);
  const prefix = approx ? '~' : '';
  const months = Math.floor(y * 12);
  if (months < 1) return `${prefix}< 1 mo`;
  if (months < 24) return `${prefix}${months} ${months === 1 ? 'mo' : 'mos'}`;
  const tenths = Math.floor(y * 10) / 10;
  return `${prefix}${Number.isInteger(tenths) ? tenths : tenths.toFixed(1)} yrs`;
}

export const formatLiveTenure = (row: any): string | null => {
  const t = liveTenure(row);
  return t ? formatTenure(t.years, t.approx) : null;
};
